import { createFeatureSelector, createSelector } from '@ngrx/store';
import { PolicyState } from './policy.reducer';
import {
  PolicyDomainOption,
  PolicyResourceOption,
} from '../models/policy-schema.model';

/**
 * Feature selector
 */
export const selectPolicyState = createFeatureSelector<PolicyState>('policy');

/**
 * Schema Selectors
 */
export const selectSchema = createSelector(
  selectPolicyState,
  state => state.schema
);

export const selectSchemaLoading = createSelector(
  selectPolicyState,
  state => state.schemaLoading
);

export const selectSchemaError = createSelector(
  selectPolicyState,
  state => state.schemaError
);

/**
 * Users Selectors
 */
export const selectUsers = createSelector(
  selectPolicyState,
  state => state.users
);

export const selectUsersLoading = createSelector(
  selectPolicyState,
  state => state.usersLoading
);

export const selectUsersError = createSelector(
  selectPolicyState,
  state => state.usersError
);

/**
 * Domains Selector
 */
export const selectDomains = createSelector(
  selectSchema,
  schema => schema?.domains || []
);

/**
 * Form Selectors
 */
export const selectForm = createSelector(
  selectPolicyState,
  state => state.form
);

export const selectFormDirty = createSelector(
  selectPolicyState,
  state => state.formDirty
);

export const selectSelectedDomain = createSelector(
  selectForm,
  form => form.domain
);

export const selectSelectedResource = createSelector(
  selectForm,
  form => form.resource
);

export const selectSelectedAction = createSelector(
  selectForm,
  form => form.action
);

export const selectSelectedScope = createSelector(
  selectForm,
  form => form.scope
);

/**
 * Derived Selectors
 */

// Get current domain object
export const selectCurrentDomain = createSelector(
  selectDomains,
  selectSelectedDomain,
  (domains, selectedDomain): PolicyDomainOption | null => {
    if (!selectedDomain) return null;
    return domains.find(d => d.value === selectedDomain) || null;
  }
);

// Get available resources for selected domain
export const selectAvailableResources = createSelector(
  selectCurrentDomain,
  (domain): PolicyResourceOption[] => {
    return domain?.resources || [];
  }
);

// Get current resource object
export const selectCurrentResource = createSelector(
  selectAvailableResources,
  selectSelectedResource,
  (resources, selectedResource): PolicyResourceOption | null => {
    if (!selectedResource) return null;
    return resources.find(r => r.value === selectedResource) || null;
  }
);

// Get available actions for selected resource
export const selectAvailableActions = createSelector(
  selectCurrentResource,
  resource => {
    return resource?.actions || [];
  }
);

// Get available scopes for selected resource
export const selectAvailableScopes = createSelector(
  selectCurrentResource,
  resource => {
    return resource?.scopes || [];
  }
);

// Check if scope is required
export const selectScopeRequired = createSelector(
  selectCurrentResource,
  resource => {
    return resource?.scopeRequired || false;
  }
);

/**
 * Form Validation Selectors
 */
export const selectFormValid = createSelector(
  selectForm,
  selectScopeRequired,
  (form, scopeRequired) => {
    const hasRequiredFields = !!(form.domain && form.resource && form.action);

    if (scopeRequired) {
      return hasRequiredFields && !!form.scope;
    }

    return hasRequiredFields;
  }
);

/**
 * Submit Selectors
 */
export const selectSubmitting = createSelector(
  selectPolicyState,
  state => state.submitting
);

export const selectSubmitError = createSelector(
  selectPolicyState,
  state => state.submitError
);

/**
 * Loading state selector (combines schema loading and submitting)
 */
export const selectIsLoading = createSelector(
  selectSchemaLoading,
  selectSubmitting,
  (schemaLoading, submitting) => schemaLoading || submitting
);

/**
 * Policy List Selectors
 */
export const selectPolicies = createSelector(
  selectPolicyState,
  state => state.policies
);

export const selectListLoading = createSelector(
  selectPolicyState,
  state => state.listLoading
);

export const selectListError = createSelector(
  selectPolicyState,
  state => state.listError
);

export const selectPagination = createSelector(
  selectPolicyState,
  state => state.pagination
);

export const selectFilters = createSelector(
  selectPolicyState,
  state => state.filters
);

export const selectSort = createSelector(
  selectPolicyState,
  state => state.sort
);

export const selectStatusFilter = createSelector(
  selectFilters,
  filters => filters.status
);

export const selectDomainFilter = createSelector(
  selectFilters,
  filters => filters.domain
);
